/**
 * Operator drive (personal + shared) — Admin / Production mini Drive library.
 * Collection: operatorDriveItems/{itemId}
 */

import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore';
import { db } from './firebase';
import { userHasAdminOrProductionRole } from './adminDesktopNotificationService';
import {
    deleteOperatorDriveStorageObject,
    DRIVE_SCOPE,
    getOperatorDriveDownloadUrl,
    uploadOperatorDriveFile,
    validateOperatorDriveFile,
} from './operatorDriveFileService';
import { getGeneralPrintSize } from '../utils/generalPrintSizes';
import { ensureDriveFileExtension, driveDownloadFileName } from '../utils/operatorDriveFileName';
import { fetchDriveStorageUrl } from '../utils/operatorDriveShareDownload';
import {
    driveItemPathLabel,
    driveItemsById,
    listDriveDescendantFiles,
    searchDriveLibrary,
    sumDriveLibraryBytes,
} from '../utils/operatorDriveLibrary';

export const OPERATOR_DRIVE_ITEMS_COLL = 'operatorDriveItems';

export const DRIVE_ITEM_TYPE = {
    FILE: 'file',
    FOLDER: 'folder',
};

export const DRIVE_NAV = {
    MY: 'my',
    SHARED: 'shared',
    RECENT: 'recent',
    STARRED: 'starred',
    TRASH: 'trash',
};

function toMillis(value) {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function mapItem(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        ...data,
        name: data.name || '',
        parentId: data.parentId || null,
        scope: data.scope || DRIVE_SCOPE.PERSONAL,
        type: data.type || DRIVE_ITEM_TYPE.FILE,
        starred: Boolean(data.starred),
        trashed: Boolean(data.trashed),
        createdAt: data.createdAt || null,
        updatedAt: data.updatedAt || null,
        trashedAt: data.trashedAt || null,
    };
}

export function sortDriveItems(a, b, sortKey = 'name') {
    const aFolder = a.type === DRIVE_ITEM_TYPE.FOLDER ? 0 : 1;
    const bFolder = b.type === DRIVE_ITEM_TYPE.FOLDER ? 0 : 1;
    if (aFolder !== bFolder) return aFolder - bFolder;

    if (sortKey === 'modified') {
        return toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt);
    }
    if (sortKey === 'size') {
        return (Number(b.sizeBytes) || 0) - (Number(a.sizeBytes) || 0);
    }
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        sensitivity: 'base',
    });
}

function assertEligible(userData) {
    if (!userHasAdminOrProductionRole(userData || {})) {
        throw new Error('Only Admin and Production can use the operator drive.');
    }
}

function normalizeName(name, contentType) {
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Name is required.');
    return ensureDriveFileExtension(trimmed, contentType);
}

function assertCanEdit(item, uid) {
    if (item.scope === DRIVE_SCOPE.PERSONAL && item.ownerUid !== uid) {
        throw new Error('You can only change your own personal files.');
    }
}

function notTrashed(item) {
    return !item.trashed;
}

/**
 * Format modified time for drive rows (Europe/London).
 */
export function formatDriveModified(value) {
    const ms = toMillis(value);
    if (!ms) return '—';
    try {
        return new Intl.DateTimeFormat('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: 'Europe/London',
        }).format(new Date(ms));
    } catch {
        return new Date(ms).toLocaleString();
    }
}

function buildFolderQuery({ scope, ownerUid, parentId }) {
    const parentKey = parentId || null;
    if (scope === DRIVE_SCOPE.SHARED) {
        return query(
            collection(db, OPERATOR_DRIVE_ITEMS_COLL),
            where('scope', '==', DRIVE_SCOPE.SHARED),
            where('parentId', '==', parentKey)
        );
    }
    return query(
        collection(db, OPERATOR_DRIVE_ITEMS_COLL),
        where('scope', '==', DRIVE_SCOPE.PERSONAL),
        where('ownerUid', '==', ownerUid),
        where('parentId', '==', parentKey)
    );
}

/**
 * One-shot list of children in a folder (excludes trash).
 */
export async function listDriveChildren({ scope, ownerUid, parentId = null }) {
    const snap = await getDocs(buildFolderQuery({ scope, ownerUid, parentId }));
    return snap.docs.map(mapItem).filter(notTrashed).sort(sortDriveItems);
}

/**
 * Subscribe to children of a folder (or root) in a scope.
 */
export function subscribeDriveFolder({ scope, ownerUid, parentId, onChange }) {
    if (!ownerUid) {
        onChange([]);
        return () => {};
    }

    return onSnapshot(
        buildFolderQuery({ scope, ownerUid, parentId }),
        (snap) => {
            const items = snap.docs.map(mapItem).filter(notTrashed).sort(sortDriveItems);
            onChange(items);
        },
        (err) => {
            console.error('[operatorDrive] folder subscribe failed:', err);
            onChange([]);
        }
    );
}

/**
 * Subscribe to all personal + shared items the user can see (for Recent / Starred / Trash).
 */
export function subscribeDriveLibrary(uid, onChange) {
    if (!uid) {
        onChange([]);
        return () => {};
    }

    let personal = [];
    let shared = [];

    const emit = () => {
        const byId = new Map();
        [...personal, ...shared].forEach((item) => {
            if (item?.id) byId.set(item.id, item);
        });
        onChange(Array.from(byId.values()));
    };

    const qPersonal = query(
        collection(db, OPERATOR_DRIVE_ITEMS_COLL),
        where('scope', '==', DRIVE_SCOPE.PERSONAL),
        where('ownerUid', '==', uid)
    );
    const qShared = query(
        collection(db, OPERATOR_DRIVE_ITEMS_COLL),
        where('scope', '==', DRIVE_SCOPE.SHARED)
    );

    const unsubP = onSnapshot(
        qPersonal,
        (snap) => {
            personal = snap.docs.map(mapItem);
            emit();
        },
        (err) => {
            console.error('[operatorDrive] personal library failed:', err);
            personal = [];
            emit();
        }
    );
    const unsubS = onSnapshot(
        qShared,
        (snap) => {
            shared = snap.docs.map(mapItem);
            emit();
        },
        (err) => {
            console.error('[operatorDrive] shared library failed:', err);
            shared = [];
            emit();
        }
    );

    return () => {
        unsubP();
        unsubS();
    };
}

/**
 * Filter library items for a Drive nav section.
 */
export function filterDriveNavItems(allItems, nav, { scope, parentId, sortKey = 'name' } = {}) {
    const list = Array.isArray(allItems) ? allItems : [];

    if (nav === DRIVE_NAV.TRASH) {
        return list.filter((i) => i.trashed).sort((a, b) => toMillis(b.trashedAt) - toMillis(a.trashedAt));
    }

    const active = list.filter(notTrashed);

    if (nav === DRIVE_NAV.STARRED) {
        return active.filter((i) => i.starred).sort((a, b) => sortDriveItems(a, b, sortKey));
    }

    if (nav === DRIVE_NAV.RECENT) {
        return active
            .filter((i) => i.type === DRIVE_ITEM_TYPE.FILE)
            .sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt))
            .slice(0, 80);
    }

    const scoped = active.filter((i) => i.scope === scope);
    const parentKey = parentId || null;
    return scoped
        .filter((i) => (i.parentId || null) === parentKey)
        .sort((a, b) => sortDriveItems(a, b, sortKey));
}

/**
 * Load ancestors for breadcrumb (skip trashed parents).
 */
export async function loadDriveBreadcrumb(folderId) {
    const chain = [];
    let currentId = folderId || null;
    let guard = 0;
    while (currentId && guard < 40) {
        guard += 1;
        const snap = await getDoc(doc(db, OPERATOR_DRIVE_ITEMS_COLL, currentId));
        if (!snap.exists()) break;
        const item = mapItem(snap);
        if (item.trashed) break;
        chain.unshift(item);
        currentId = item.parentId || null;
    }
    return chain;
}

/**
 * Create a folder.
 */
export async function createDriveFolder({
    scope,
    ownerUid,
    parentId = null,
    name,
    userData,
}) {
    assertEligible(userData);
    if (!ownerUid) throw new Error('ownerUid is required.');
    const folderName = normalizeName(name);
    const itemRef = doc(collection(db, OPERATOR_DRIVE_ITEMS_COLL));
    const now = serverTimestamp();
    const payload = {
        type: DRIVE_ITEM_TYPE.FOLDER,
        name: folderName,
        parentId: parentId || null,
        scope: scope === DRIVE_SCOPE.SHARED ? DRIVE_SCOPE.SHARED : DRIVE_SCOPE.PERSONAL,
        ownerUid: scope === DRIVE_SCOPE.SHARED ? null : ownerUid,
        createdBy: ownerUid,
        createdAt: now,
        updatedAt: now,
        starred: false,
        trashed: false,
        trashedAt: null,
    };
    await setDoc(itemRef, payload);
    return { id: itemRef.id, ...payload };
}

/**
 * Rename a file or folder.
 */
export async function renameDriveItem({ itemId, name, uid, userData }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const newName = normalizeName(name);
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) throw new Error('Item not found.');
    const item = mapItem(snap);
    assertCanEdit(item, uid);
    await updateDoc(itemRef, {
        name: newName,
        updatedAt: serverTimestamp(),
        renamedBy: uid,
    });
    return { ...item, name: newName };
}

/**
 * Move item into another folder (same scope).
 */
export async function moveDriveItem({ itemId, parentId, uid, userData }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) throw new Error('Item not found.');
    const item = mapItem(snap);
    assertCanEdit(item, uid);
    if (parentId === itemId) throw new Error('Cannot move a folder into itself.');
    if (parentId) {
        const parentSnap = await getDoc(doc(db, OPERATOR_DRIVE_ITEMS_COLL, parentId));
        if (!parentSnap.exists()) throw new Error('Destination folder not found.');
        const parent = mapItem(parentSnap);
        if (parent.type !== DRIVE_ITEM_TYPE.FOLDER) throw new Error('Destination must be a folder.');
        if (parent.scope !== item.scope) throw new Error('Stay within the same drive (My files or Shared).');
        if (parent.trashed) throw new Error('Destination folder is in Trash.');
    }
    await updateDoc(itemRef, {
        parentId: parentId || null,
        updatedAt: serverTimestamp(),
        movedBy: uid,
    });
}

export async function setDriveItemPrintSize({ itemId, printSizeKey, uid, userData }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const size = getGeneralPrintSize(printSizeKey);
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) throw new Error('Item not found.');
    const item = mapItem(snap);
    assertCanEdit(item, uid);
    if (item.type !== DRIVE_ITEM_TYPE.FILE) {
        throw new Error('Print size is only stored on files.');
    }
    await updateDoc(itemRef, {
        printSizeKey: size.key,
        updatedAt: serverTimestamp(),
        printSizeBy: uid,
    });
}

/**
 * Duplicate a file in the same folder (new Storage object + new item).
 */
export async function duplicateDriveFile({ itemId, uid, userData, onProgress }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const snap = await getDoc(doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId));
    if (!snap.exists()) throw new Error('Item not found.');
    const item = mapItem(snap);
    assertCanEdit(item, uid);
    if (item.type !== DRIVE_ITEM_TYPE.FILE) {
        throw new Error('Only files can be duplicated.');
    }
    if (item.trashed) throw new Error('Restore the file from Trash before duplicating.');
    const url = item.downloadUrl || (item.storagePath
        ? await getOperatorDriveDownloadUrl(item.storagePath)
        : '');
    if (!url) throw new Error('File has no storage path.');
    const response = await fetchDriveStorageUrl(url, item.name || 'the file');
    const blob = await response.blob();
    const file = new File([blob], driveDownloadFileName(item), {
        type: item.contentType || blob.type || '',
    });
    const copyName = ensureDriveFileExtension(
        `Copy of ${item.name || item.originalFileName || 'file'}`,
        item.contentType || blob.type
    );
    return uploadDriveFile({
        file,
        scope: item.scope,
        ownerUid: item.ownerUid || uid,
        parentId: item.parentId || null,
        displayName: copyName,
        userData,
        onProgress,
    });
}

export async function setDriveItemStarred({ itemId, starred, uid, userData }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) throw new Error('Item not found.');
    assertCanEdit(mapItem(snap), uid);
    await updateDoc(itemRef, {
        starred: Boolean(starred),
        updatedAt: serverTimestamp(),
    });
}

export async function trashDriveItem({ itemId, uid, userData }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) return;
    assertCanEdit(mapItem(snap), uid);
    await updateDoc(itemRef, {
        trashed: true,
        trashedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        trashedBy: uid,
    });
}

export async function restoreDriveItem({ itemId, uid, userData }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) return;
    assertCanEdit(mapItem(snap), uid);
    await updateDoc(itemRef, {
        trashed: false,
        trashedAt: null,
        updatedAt: serverTimestamp(),
        restoredBy: uid,
    });
}

/**
 * Upload one file into the current folder.
 */
export async function uploadDriveFile({
    file,
    scope,
    ownerUid,
    parentId = null,
    displayName,
    userData,
    onProgress,
}) {
    assertEligible(userData);
    if (!ownerUid) throw new Error('ownerUid is required.');
    const fileErr = validateOperatorDriveFile(file);
    if (fileErr) throw new Error(fileErr);

    const itemRef = doc(collection(db, OPERATOR_DRIVE_ITEMS_COLL));
    const resolvedName = normalizeName(
        displayName || file.name || 'file',
        file.type
    );

    const uploaded = await uploadOperatorDriveFile({
        file,
        scope: scope === DRIVE_SCOPE.SHARED ? DRIVE_SCOPE.SHARED : DRIVE_SCOPE.PERSONAL,
        ownerUid,
        itemId: itemRef.id,
        onProgress,
    });

    const now = serverTimestamp();
    const payload = {
        type: DRIVE_ITEM_TYPE.FILE,
        name: resolvedName,
        parentId: parentId || null,
        scope: scope === DRIVE_SCOPE.SHARED ? DRIVE_SCOPE.SHARED : DRIVE_SCOPE.PERSONAL,
        ownerUid: scope === DRIVE_SCOPE.SHARED ? null : ownerUid,
        createdBy: ownerUid,
        createdAt: now,
        updatedAt: now,
        starred: false,
        trashed: false,
        trashedAt: null,
        storagePath: uploaded.storagePath,
        downloadUrl: uploaded.downloadUrl,
        contentType: uploaded.contentType,
        sizeBytes: uploaded.sizeBytes,
        originalFileName: uploaded.fileName,
    };
    await setDoc(itemRef, payload);
    return { id: itemRef.id, ...payload };
}

/**
 * Upload many files, preserving relative folder paths from a directory picker.
 */
export async function uploadDriveFilesWithPaths({
    files,
    scope,
    ownerUid,
    parentId = null,
    userData,
    onFileProgress,
}) {
    assertEligible(userData);
    const list = Array.from(files || []);
    if (!list.length) return [];

    /** @type {Map<string, string>} */
    const folderCache = new Map();
    const created = [];

    const ensureFolderPath = async (segments, baseParentId) => {
        let currentParent = baseParentId || null;
        let pathKey = `${scope}:${ownerUid}:${currentParent || 'root'}`;
        for (const segment of segments) {
            const name = normalizeName(segment);
            pathKey = `${pathKey}/${name.toLowerCase()}`;
            if (folderCache.has(pathKey)) {
                currentParent = folderCache.get(pathKey);
                continue;
            }
            const siblings = await listDriveChildren({
                scope,
                ownerUid,
                parentId: currentParent,
            });
            const existing = siblings.find(
                (s) =>
                    s.type === DRIVE_ITEM_TYPE.FOLDER &&
                    String(s.name).toLowerCase() === name.toLowerCase()
            );
            if (existing) {
                folderCache.set(pathKey, existing.id);
                currentParent = existing.id;
                continue;
            }
            const folder = await createDriveFolder({
                scope,
                ownerUid,
                parentId: currentParent,
                name,
                userData,
            });
            folderCache.set(pathKey, folder.id);
            currentParent = folder.id;
        }
        return currentParent;
    };

    for (let i = 0; i < list.length; i += 1) {
        const file = list[i];
        const rel = String(file.webkitRelativePath || file.name || '').replace(/\\/g, '/');
        const parts = rel.split('/').filter(Boolean);
        const fileName = parts.pop() || file.name || 'file';
        const folderSegments = parts;
        const targetParent = folderSegments.length
            ? await ensureFolderPath(folderSegments, parentId)
            : parentId || null;

        const item = await uploadDriveFile({
            file,
            scope,
            ownerUid,
            parentId: targetParent,
            displayName: fileName,
            userData,
            onProgress: (pct) => {
                if (typeof onFileProgress === 'function') {
                    onFileProgress({
                        index: i,
                        total: list.length,
                        fileName,
                        pct,
                    });
                }
            },
        });
        created.push(item);
    }

    return created;
}

/**
 * Storage download URL for staff print / Open inside Smoke Drive.
 * Do not copy this to people outside the desk — use Internal / External website shares.
 */
export async function getDriveShareLink(itemId) {
    if (!itemId) throw new Error('itemId is required.');
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) throw new Error('Item not found.');
    const item = mapItem(snap);
    if (item.type !== DRIVE_ITEM_TYPE.FILE) {
        throw new Error('Only files have download links.');
    }
    if (item.downloadUrl) return item.downloadUrl;
    if (!item.storagePath) throw new Error('File has no storage path.');
    const url = await getOperatorDriveDownloadUrl(item.storagePath);
    await updateDoc(itemRef, { downloadUrl: url, updatedAt: serverTimestamp() });
    return url;
}

/**
 * Permanently delete (from Trash or empty folder hard-delete).
 */
export async function permanentlyDeleteDriveItem({ itemId, uid, userData }) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) return;
    const item = mapItem(snap);
    assertCanEdit(item, uid);
    if (item.type === DRIVE_ITEM_TYPE.FOLDER) {
        const children = await listDriveChildren({
            scope: item.scope,
            ownerUid: item.ownerUid || uid,
            parentId: itemId,
        });
        const trashKids = (
            await getDocs(buildFolderQuery({ scope: item.scope, ownerUid: item.ownerUid || uid, parentId: itemId }))
        ).docs.map(mapItem);
        if (children.length > 0 || trashKids.some((c) => c.trashed)) {
            throw new Error('Empty the folder (including Trash children) before deleting forever.');
        }
    }
    if (item.type === DRIVE_ITEM_TYPE.FILE && item.storagePath) {
        await deleteOperatorDriveStorageObject(item.storagePath);
    }
    await deleteDoc(itemRef);
}

/** @deprecated use trashDriveItem — kept for callers expecting hard delete name */
export async function deleteDriveItem(args) {
    return trashDriveItem(args);
}

export {
    DRIVE_SCOPE,
    userHasAdminOrProductionRole,
    driveItemPathLabel,
    driveItemsById,
    listDriveDescendantFiles,
    searchDriveLibrary,
    sumDriveLibraryBytes,
};
